import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DocumentType, OwnedDocument } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL } from '@pelicargo/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiChevronLeft, HiOutlineArrowUpTray, HiOutlineCheck } from 'react-icons/hi2';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { buildAwbDocumentLabels } from '../../utils/awbDocumentLabels';
import { documentSchema } from '../../utils/schemas';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleForm } from '../SimpleForm';
import { DocumentGroups } from './DocumentGroups';

type Props = {
  defaultDocuments?: OwnedDocument[];
  documentTypes?: DocumentType[];
  isApprovable?: boolean;
  isEmptyDocumentsAllowed?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit?: () => void;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  backLabel?: string;
  mode?: 'manage' | 'submit';
  /** Show a Back/Cancel button alongside the manage-mode action buttons. */
  showBack?: boolean;
};

type DocumentFormValues = {
  documents: OwnedDocument[];
};

const DEFAULT_SUBMIT_SUBTITLE = 'Please upload any other documents.';

export const DocumentsModal = ({
  isOpen,
  onClose,
  onBack,
  onSubmit,
  documentTypes,
  defaultDocuments: documents = [],
  isApprovable = false,
  isEmptyDocumentsAllowed = false,
  title = 'Manage Documents',
  subtitle,
  submitLabel = 'Update',
  backLabel = 'Back',
  mode = 'manage',
  showBack = false,
}: Props) => {
  const { requestId, quoteId } = useParams<{ requestId: string; quoteId: string }>();
  const { data: shipmentForms } = trpc.getShipmentFormsByRequestId.useQuery(
    { requestId: Number(requestId), quoteId: Number(quoteId) },
    { enabled: !!requestId && !!quoteId, refetchOnWindowFocus: false },
  );
  const awbLabelByDocumentId = useMemo(() => buildAwbDocumentLabels(shipmentForms), [shipmentForms]);
  const { mutateAsync: updateDocuments, isLoading } = trpc.updateDocuments.useMutation();
  const { mutateAsync: approveDocuments, isLoading: isApproving } = trpc.approveDocuments.useMutation();
  const methods = useForm<DocumentFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(z.object({ documents: z.array(documentSchema).min(1) })),
    defaultValues: { documents },
  });
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const { targetAccount } = useTargetAccount();

  const currentDocuments = methods.watch('documents').filter(Boolean);

  // The form's copy once it has one, so newly dropped files show up immediately.
  const statusDocuments = currentDocuments.length ? currentDocuments : documents;

  const resolvedSubtitle = subtitle ?? (mode === 'submit' ? DEFAULT_SUBMIT_SUBTITLE : undefined);
  const updatedDocuments = currentDocuments.filter((doc) => {
    const existingDoc = documents.find((d) => d.id === doc?.id);
    return existingDoc && doc.type !== existingDoc.type && !!doc.type;
  });
  const isUpdateButtonsDisabled = updatedDocuments.length === 0;
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(!isEmptyDocumentsAllowed);
  const nonAccountDocuments = currentDocuments.filter((doc) => doc.account_id !== targetAccount?.id);
  const isApproveButtonDisabled = nonAccountDocuments
    .filter((doc) => DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type))
    .every((doc) => !!doc.approved_at);

  useEffect(() => {
    if (documents.length) methods.setValue('documents', documents);
  }, [documents, documents.length, methods]);

  const handleUpdate = async () => {
    await updateDocuments({
      documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
      quote_id: Number(quoteId),
    });
    onClose();
  };

  const handleUpdateAndNotify = async () => {
    try {
      setIsNotifyLoading(true);
      await updateDocuments({
        documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
        quote_id: Number(quoteId),
        shouldNotify: true,
      });

      onClose();
    } finally {
      setIsNotifyLoading(false);
    }
  };
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (updatedDocuments.length > 0) {
      await updateDocuments({
        documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
        quote_id: Number(quoteId),
      });
    }

    if (onSubmit) {
      onSubmit();
      return;
    }

    onClose();
  };

  const handleApproveAndNotify = async () => {
    const data = methods.getValues();
    const nonApprovedDocuments = data.documents.filter(
      (doc) =>
        doc.id &&
        !doc.approved_at &&
        doc.account_id !== targetAccount?.id &&
        DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type),
    );
    await approveDocuments({
      documentIds: nonApprovedDocuments.map((doc) => doc.id),
      quote_id: Number(quoteId),
      shouldNotify: true,
    });

    onClose();
  };

  const handleDocumentsChange = (documents: OwnedDocument[]) => {
    setIsSubmitButtonDisabled(documents.length === 0 && !isEmptyDocumentsAllowed);
  };

  return (
    <Modal size={{ base: 'full', lg: '2xl' }} isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxH={{ base: 'full', lg: '83vh' }} overflow="auto" borderRadius={{ base: '0', lg: 'md' }}>
        <SimpleForm onSubmit={mode === 'submit' ? handleSubmit : handleUpdate} {...methods}>
          <ModalCloseButton />
          <ModalHeader pb="2">
            <Stack spacing="1">
              <Heading variant="h4">{title}</Heading>
              <Show if={!!resolvedSubtitle}>
                <Text color="text.subtle" fontSize="sm">
                  {resolvedSubtitle}
                </Text>
              </Show>
            </Stack>
          </ModalHeader>

          <ModalBody pb="2">
            <Stack spacing="4">
              <DocumentGroups
                documents={statusDocuments}
                requestId={Number(requestId)}
                expectedTypes={documentTypes}
                labelByDocumentId={awbLabelByDocumentId}
              />

              <Divider />

              <SimpleDropzone
                name="documents"
                requestId={Number(requestId)}
                isTypeSelectable
                onDocumentsChange={handleDocumentsChange}
              />
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Show if={mode === 'submit'}>
              <Stack direction="row" justify="space-between" w="full">
                <Button size="sm" variant="outline" onClick={handleBack} leftIcon={<HiChevronLeft />}>
                  {backLabel}
                </Button>
                <Button
                  size="sm"
                  colorScheme="primary"
                  onClick={handleSubmit}
                  isLoading={isLoading && !isNotifyLoading}
                  isDisabled={isSubmitButtonDisabled}
                >
                  {submitLabel}
                </Button>
              </Stack>
            </Show>

            <Show if={mode === 'manage'}>
              <Stack width="full" justify="space-between" align="center" direction="row" spacing="3">
                <Show if={showBack}>
                  <Button variant="outline" onClick={handleBack} leftIcon={<HiChevronLeft />}>
                    {backLabel}
                  </Button>
                </Show>
                <Stack justify="flex-end" direction="row" spacing="3" flex="1">
                  <Button
                    variant="outline"
                    colorScheme="primary"
                    title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                    onClick={handleUpdate}
                    isLoading={isLoading && !isNotifyLoading}
                    isDisabled={isUpdateButtonsDisabled}
                    leftIcon={<HiOutlineArrowUpTray />}
                  >
                    {submitLabel}
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="primary"
                    title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                    onClick={handleUpdateAndNotify}
                    isLoading={isNotifyLoading}
                    isDisabled={isUpdateButtonsDisabled}
                    leftIcon={<HiOutlineArrowUpTray />}
                  >
                    Update & Notify
                  </Button>
                  <Show if={isApprovable}>
                    <Button
                      variant="solid"
                      colorScheme="green"
                      title={isApproveButtonDisabled ? 'No documents to approve' : undefined}
                      onClick={handleApproveAndNotify}
                      isLoading={isApproving}
                      isDisabled={isApproveButtonDisabled}
                      leftIcon={<HiOutlineCheck />}
                    >
                      Approve & Notify
                    </Button>
                  </Show>
                </Stack>
              </Stack>
            </Show>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
